import {CommonModule} from '@angular/common';
import {ChangeDetectionStrategy, Component, inject, Input} from '@angular/core';
import {filterNullishRx, fromControl, TranslatePipe} from '@brunata-angular/util';
import {
    combineLatest,
    map,
    Observable,
    pairwise,
    ReplaySubject,
    startWith,
    switchMap,
    tap,
} from 'rxjs';
import {ReactiveFormsModule} from '@angular/forms';
import {InputSelectOptionGroupComponent} from '@brunata-angular/ui';
import {InputTextGroupComponent} from '@brunata-angular/ui';
import {
    AgentMeterSettingsData,
    AgentMeterSettingsFormGroup,
} from '../select-meters-for-agent.model';
import {InputCheckboxesComponent} from '@brunata-angular/ui';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {SelectMetersForAgentService} from '../select-meters-for-agent.service';

type SelectMetersSettingsView = AgentMeterSettingsData & {
    formGroup: AgentMeterSettingsFormGroup;
    compareGroups: boolean;
    outputUnitsData: Map<string, string>;
};

@Component({
    selector: 'app-select-meters-settings',
    standalone: true,
    imports: [
        TranslatePipe,
        CommonModule,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputSelectOptionGroupComponent,
        InputCheckboxesComponent,
    ],
    templateUrl: './select-meters-settings.component.html',
    styleUrl: './select-meters-settings.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectMetersSettingsComponent {
    @Input({required: true}) set formGroup(value: AgentMeterSettingsFormGroup) {
        this.formGroupSubject$.next(value);
    }

    @Input({required: true}) set settings(value: AgentMeterSettingsData) {
        this.settingsSubject$.next(value);
    }

    private readonly meterForAgentService = inject(SelectMetersForAgentService);

    private readonly formGroupSubject$ = new ReplaySubject<AgentMeterSettingsFormGroup | undefined>(
        1,
    );

    private readonly settingsSubject$ = new ReplaySubject<AgentMeterSettingsData | undefined>(1);

    private readonly formGroup$: Observable<AgentMeterSettingsFormGroup> =
        this.formGroupSubject$.pipe(filterNullishRx());

    private readonly formGroupChange$: Observable<AgentMeterSettingsFormGroup> =
        this.formGroup$.pipe(
            switchMap(formGroup =>
                formGroup.valueChanges.pipe(
                    startWith(formGroup),
                    map(() => formGroup),
                ),
            ),
        );

    private readonly settings$ = this.settingsSubject$.pipe(filterNullishRx());

    private readonly selectedSuperType$ = this.formGroup$.pipe(
        switchMap(({controls}) =>
            fromControl(controls.meterSuperTypeNo).pipe(
                startWith(''),
                pairwise(),
                tap(([previousSuperType, _]) => {
                    if (previousSuperType === '') {
                        // Skip resetting if modal was just opened to preserve previous selections.
                        return;
                    }
                    controls.outputUnit.reset(undefined, {emitEvent: false});
                    controls.selectableUsageAndUnits.reset(undefined, {emitEvent: false});
                }),
                map(([_, selectedSuperType]) => selectedSuperType),
            ),
        ),
    );

    private readonly outputUnitData$: Observable<Map<string, string>> = combineLatest([
        this.settings$,
        this.selectedSuperType$,
    ]).pipe(
        map(
            ([settings, selectedSuperType]) =>
                new Map<string, string>([
                    ['', ''],
                    ...(settings.superTypeToConvertableUnitsTranslationMap
                        .get(selectedSuperType)
                        ?.entries() || []),
                ]),
        ),
    );

    private readonly selectedOutputUnit$: Observable<string> = this.formGroup$.pipe(
        switchMap(({controls}) =>
            fromControl(controls.outputUnit).pipe(
                startWith(''),
                pairwise(),
                tap(([previousOutputUnit, _]) => {
                    if (previousOutputUnit === '') {
                        // Skip resetting if modal was just opened to preserve previous selections.
                        return;
                    }
                    controls.selectableUsageAndUnits.reset(undefined, {emitEvent: false});
                }),
                map(([_, selectedOutputUnit]) => String(selectedOutputUnit)),
            ),
        ),
    );

    private readonly convertableUnits$ = combineLatest([
        this.settings$,
        this.selectedOutputUnit$,
    ]).pipe(
        map(
            ([settingsData, selectedOutputUnit]) =>
                settingsData.unitToConvertableUnitsMap.get(selectedOutputUnit) ?? [],
        ),
    );

    readonly view$: Observable<SelectMetersSettingsView> = combineLatest([
        this.formGroupChange$,
        this.settings$,
        this.meterForAgentService.compareGroups$,
        this.outputUnitData$,
    ]).pipe(
        map(([formGroup, settingsData, compareGroups, outputUnitsData]) => ({
            formGroup,
            ...settingsData,
            compareGroups,
            outputUnitsData,
        })),
    );

    constructor() {
        combineLatest([this.settings$, this.convertableUnits$, this.formGroup$])
            .pipe(takeUntilDestroyed())
            .subscribe(([settingsData, convertableUnits, {controls}]) => {
                const {meterSuperTypeNo, selectableUsageAndUnits} = controls;

                const superType = meterSuperTypeNo.value;
                const meterTypes =
                    settingsData.superTypeToMeterTypeTranslationMap.get(superType) ?? [];

                const usageAndUnits = meterTypes
                    .filter(type => convertableUnits.includes(type.unit))
                    .map(meterType => ({
                        name: meterType.usageAndUnit,
                        alloUnitType: meterType.alloUnitType,
                        unit: meterType.unit,
                        selected:
                            selectableUsageAndUnits.value.find(
                                item => item.name === meterType.usageAndUnit,
                            )?.selected ?? false,
                    }));

                selectableUsageAndUnits.setValue(usageAndUnits, {emitEvent: false});
            });
    }
}
